import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import Seo from "@/components/Seo";
import { ArrowLeft } from "lucide-react";
import { renderMarkdown } from "@/lib/markdown";

export default function GuideDetailPage() {
  const { slug } = useParams();
  const [guide, setGuide] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    api.get(`/guides/slug/${slug}`)
      .then((r) => setGuide(r.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="container-x py-20 text-[hsl(var(--text-muted))]">Chargement…</div>;
  if (notFound) return (
    <div className="container-x py-20 text-center" data-testid="guide-not-found">
      <p className="font-serif text-3xl">Guide introuvable</p>
      <Link to="/guides" className="mt-4 inline-block text-[hsl(var(--brand-primary))] hover:underline">← Retour aux guides</Link>
    </div>
  );

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: guide.title,
    description: guide.excerpt,
    keywords: (guide.keywords || []).join(", "),
    inLanguage: "fr-FR",
    author: { "@type": "Organization", name: "Recette-Diabete" },
    datePublished: guide.created_at,
    dateModified: guide.updated_at,
  };

  return (
    <>
      <Seo title={guide.title} description={guide.excerpt} keywords={guide.keywords} path={`/guides/${guide.slug}`} type="article" image={guide.image_url} jsonLd={jsonLd} />

      <article className="container-x pt-12 pb-24" data-testid="guide-article">
        <Link to="/guides" className="inline-flex items-center gap-1 text-xs uppercase tracking-wider text-[hsl(var(--text-muted))] hover:text-[hsl(var(--brand-primary))]">
          <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} /> Tous les guides
        </Link>

        <header className="mt-8 max-w-3xl">
          <p className="overline mb-4">{guide.category} · {guide.reading_time_min} min de lecture</p>
          <h1 className="font-serif text-5xl md:text-6xl leading-[1.05] tracking-tight" data-testid="guide-title">{guide.title}</h1>
          {guide.subtitle && (
            <p className="mt-4 font-serif italic text-2xl text-[hsl(var(--text-secondary))]">{guide.subtitle}</p>
          )}
          <p className="mt-6 text-lg text-[hsl(var(--text-secondary))]">{guide.excerpt}</p>
        </header>

        <hr className="my-12 border-[hsl(var(--border-light))]" />

        <div
          className="editorial-prose mx-auto"
          data-testid="guide-content"
          dangerouslySetInnerHTML={{ __html: renderMarkdown(guide.content || "") }}
        />
      </article>
    </>
  );
}
